import { MotifIcon } from "@ey-xd/motif-react";
import { IconoirList } from "@ey-xd/motif-icon";
import "./CancelledOrdersTable.scss";
import { ProductAccordian } from "../../../pages/Checkout/Components/OrderExpected/ProductAccordian";
import orderEmptyImg from "../../../assets/images/empty_order_pipeline.svg";
import EmptyOrder from "../../EmptyOrder";
import {
  NO_CANCELLED_ORDERS_HEADING,
  NO_CANCELLED_ORDERS_TEXT,
} from "../../../Constant";
import { getTotalOrderAmount } from "../helper";
import { RichTextEditor } from "../../Form/RichTextEditor/RichTextEditor";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import { fetchSkuConfigData } from "../../../store/slices/CTDBSkuConfigSlice";
import Loader from "../../Loader/Loader";
import NoSearchResult from "../../NoSearchResult/NoSearchResult";
import { useRef, useState, useEffect } from "react";
import { containsEmoji, sanitizeHTMLSpace } from "../../../utils/common";
import OrderItemsHeaderTooltip from "../OrderItemsHeaderTooltip/OrderItemsHeaderTooltip";

export interface ColumnConfig {
  key: string;
  label: string;
  render?: (row: any) => React.ReactNode;
}

interface CancelledOrdersTableProps {
  data: any[];
  columnsData: ColumnConfig[];
  setOrders: React.Dispatch<React.SetStateAction<any[]>>;
  setShowPagination: React.Dispatch<React.SetStateAction<boolean>>;
  searchText: string;
  isFilterApplied: boolean;
}

const CancelledOrdersTable: React.FC<CancelledOrdersTableProps> = ({
  data,
  columnsData,
  setOrders,
  setShowPagination,
  searchText,
  isFilterApplied,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const configData = useSelector(
    (state: RootState) => state.CTDBSkuConfigSlice.data,
  );
  const tableRef = useRef<HTMLTableElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const [fixedHeader, setFixedHeader] = useState(false);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);
  const [expandedTableWidth, setExpandedTableWidth] = useState<number>(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!tableRef.current) return;
      const tableTop = tableRef.current.getBoundingClientRect().top;
      setFixedHeader(tableTop < 0);
    };
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  useEffect(() => {
    const measureColumnWidths = () => {
      if (!tableRef.current) return;
      const firstRow = tableRef.current.querySelector("tr");
      if (!firstRow) return;
      const widths: number[] = Array.from(firstRow.children).map(
        (cell: any) => cell.getBoundingClientRect().width,
      );
      setColumnWidths(widths);
    };

    measureColumnWidths();
    window.addEventListener("resize", measureColumnWidths);

    const resizeObserver = new ResizeObserver(measureColumnWidths);
    if (tableRef.current) {
      resizeObserver.observe(tableRef.current);
    }

    return () => {
      window.removeEventListener("resize", measureColumnWidths);
      resizeObserver.disconnect();
    };
  }, [data, columnsData, configData]);

  useEffect(() => {
    const updateExpandedTableWidth = () => {
      if (!tableScrollRef.current) return;
      setExpandedTableWidth(tableScrollRef.current.clientWidth);
    };

    updateExpandedTableWidth();
    window.addEventListener("resize", updateExpandedTableWidth);

    const resizeObserver = new ResizeObserver(updateExpandedTableWidth);
    if (tableScrollRef.current) {
      resizeObserver.observe(tableScrollRef.current);
    }

    return () => {
      window.removeEventListener("resize", updateExpandedTableWidth);
      resizeObserver.disconnect();
    };
  }, [data, columnsData, configData]);

  const syncScroll = () => {
    if (tableScrollRef.current) {
      const scrollLeft = tableScrollRef.current.scrollLeft;
      const fixedHeaderTable =
        document.querySelector<HTMLTableElement>(".fixed-header");
      if (fixedHeaderTable) {
        fixedHeaderTable.style.transform = `translateX(-${scrollLeft}px)`;
      }
    }
  };

  const renderNoSearchResult = () => {
    if (isFilterApplied) {
      const header = searchText
        ? `No result under the filter and by the keyword "${searchText}"`
        : `No result under the filter`;

      const message = searchText
        ? "Did you not find any results? We recommend refining your search criteria and/or broadening your filter parameters to enhance your search outcomes."
        : "Did not find any results? We recommend broadening your filter criteria for a more comprehensive search.";

      return (
        <NoSearchResult
          searchValue={searchText}
          header={header}
          message={message}
        />
      );
    } else {
      return <NoSearchResult searchValue={searchText} />;
    }
  };

  if (Array.isArray(data) && data?.length === 0) {
    const hasEmoji = containsEmoji(searchText);
    if ((!searchText && !isFilterApplied) || hasEmoji) {
      return (
        <EmptyOrder
          image={orderEmptyImg}
          header={NO_CANCELLED_ORDERS_HEADING}
          subHeader={NO_CANCELLED_ORDERS_TEXT}
        />
      );
    }
    return renderNoSearchResult();
  } else {
    if (configData === null && data && data.length > 0) {
      dispatch(fetchSkuConfigData());
    }

    if (!configData) {
      return <Loader />;
    } else {
      setShowPagination(true);
    }

    const { CostCategory, CostMacroCategory } = configData;

    const getCostCataegoryType = (order: any) => {
      if (CostCategory.length === 0) {
        return null;
      }
      return CostCategory.find(
        (cost) => cost.CostMetricCd === order.CostMetricCd,
      )?.CostMetricType;
    };
    const getCostMetric = (order: any) => {
      if (CostCategory.length === 0) {
        return null;
      }
      return CostCategory.find(
        (cost) => cost.CostMetricCd === order.CostMetricCd,
      )?.CostMetric;
    };

    const getCostMacroCategory = (order: any) => {
      if (CostMacroCategory.length === 0) {
        return null;
      }
      return CostMacroCategory.filter((cost) =>
        order.CostMacroCategoryCd.includes(cost.CostMacroCategoryCd),
      )
        .map((item) => item.Name)
        .join(", ");
    };

    const getClassNamesCancelOrderTable = (key: string) => {
      switch (key) {
        case "OrderName":
          return "cancel-order-name";
        case "SKUName":
          return "cancel-sku-name";
        case "RequestorId":
          return "cancel-requestor-id";
        case "Total":
          return "cancel-total-amount text-right-align";
        case "CancelledDate":
          return "cancel-date";
        case "OrderSkuStatus":
          return "cancel-status";
        default:
          return "";
      }
    };

    return (
      <div
        className="cancelled-orders-table-container"
        style={{ position: "relative" }}
      >
        {fixedHeader && (
          <div
            className="fixed-header-wrapper"
            style={{
              overflow: "hidden",
              width: tableScrollRef.current?.getBoundingClientRect().width,
            }}
          >
            <table
              className="motif-table-standard fixed-header"
              style={{ width: columnWidths.reduce((a, b) => a + b, 0) }}
            >
              <thead>
                <tr>
                  {columnsData.map((col, index) => (
                    <th
                      className={getClassNamesCancelOrderTable(col.key)}
                      key={col.key}
                      style={{ width: columnWidths[index] }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
            </table>
          </div>
        )}
        <div
          className="table-scroll-wrapper"
          ref={tableScrollRef}
          style={{ overflowX: "auto" }}
          onScroll={syncScroll}
        >
          <table className="motif-table-standard" ref={tableRef}>
            <thead>
              <tr>
                {columnsData.map((col) => (
                  <th
                    key={col.key}
                    className={getClassNamesCancelOrderTable(col.key)}
                    style={{ minWidth: "160px" }}
                  >
                    {col.label}{" "}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.map((order) => {
                return (
                  <>
                    <tr>
                      {columnsData.map((col) => (
                        <td key={col.key}>
                          {col.render ? col.render(order) : order[col.key]}
                        </td>
                      ))}
                    </tr>
                    <tr
                      className={
                        order.isSKUExpanded
                          ? "product-list-wrapper"
                          : "product-list-wrapper hide-details"
                      }
                    >
                      <td
                        colSpan={9}
                        className="product-list order-success-content"
                      >
                        <>
                          <div className="product-list-header">
                            <MotifIcon
                              iconFunction={IconoirList}
                              size="20"
                              title="Order Bundle"
                            ></MotifIcon>
                            <span>SKU Details</span>
                          </div>
                          <div className="sku-details-content">
                            <div className="sku-details-desc-heading">
                              Description
                            </div>
                            <RichTextEditor
                              value={sanitizeHTMLSpace(order.Description)}
                              readOnly={true}
                            />
                            <div className="motif-container sku-detail-container">
                              <div className="motif-row sku-detail-row">
                                <div className="motif-col-lg-4">
                                  <div className="label">Status</div>
                                  <div className="value">
                                    {order.StatusName}
                                  </div>
                                </div>
                                <div className="motif-col-lg-4">
                                  <div className="label">Cost</div>
                                  <div className="value">
                                    {getTotalOrderAmount(order)}
                                  </div>
                                </div>
                                <div className="motif-col-lg-4">
                                  <div className="label">Returnable</div>
                                  <div className="value">
                                    {order.IsReturnable ? "Yes" : "No"}
                                  </div>
                                </div>
                              </div>
                              <div className="motif-row sku-detail-row">
                                <div className="motif-col-lg-4">
                                  <div className="label">Cost Interval</div>
                                  <div className="value">
                                    {order.CostIntervalCd}
                                  </div>
                                </div>
                                <div className="motif-col-lg-4">
                                  <div className="label">Cost Type</div>
                                  <div className="value">
                                    {getCostCataegoryType(order)}
                                  </div>
                                </div>
                                <div className="motif-col-lg-4">
                                  <div className="label">Cost Metric</div>
                                  <div className="value">
                                    {getCostMetric(order)}
                                  </div>
                                </div>
                              </div>
                              <div className="motif-row sku-detail-row">
                                <div className="motif-col-lg-4">
                                  <div className="label">
                                    Cost Macro Category
                                  </div>
                                  <div className="value">
                                    {getCostMacroCategory(order)}
                                  </div>
                                </div>
                                <div className="motif-col-lg-4">
                                  <div className="label">NSR</div>
                                  <div className="value">
                                    {order.CostMetricNSR === null
                                      ? 0
                                      : order.CostMetricNSR}
                                  </div>
                                </div>
                                <div className="motif-col-lg-4"></div>
                              </div>
                            </div>
                          </div>
                          <div className="product-list-footer">
                            <span
                              onClick={() =>
                                setOrders(
                                  data.map((o) =>
                                    o.OrderSkuId === order.OrderSkuId
                                      ? { ...o, isSKUExpanded: false }
                                      : o,
                                  ),
                                )
                              }
                            >
                              Close Details
                            </span>
                          </div>
                        </>
                      </td>
                    </tr>

                    <tr
                      className={
                        order.isOrderExpanded
                          ? "product-list-wrapper"
                          : "product-list-wrapper hide-details"
                      }
                    >
                      <td
                        colSpan={9}
                        className="product-list order-success-content order-items-list"
                      >
                        <>
                          <div className="product-list-content">
                            <table
                              className="motif-table-standard"
                              style={
                                expandedTableWidth
                                  ? {
                                      width: `${expandedTableWidth}px`,
                                      maxWidth: `${expandedTableWidth}px`,
                                    }
                                  : undefined
                              }
                            >
                              <thead>
                                <tr>
                                  <th>
                                    <OrderItemsHeaderTooltip
                                      label="SKU name/SKU ID"
                                      type="sku"
                                    />
                                  </th>
                                  <th>Engagement code</th>
                                  <th className="text-right-align">
                                    <OrderItemsHeaderTooltip
                                      label="Unit cost"
                                      type="unitCost"
                                    />
                                  </th>
                                  <th>
                                    <OrderItemsHeaderTooltip
                                      label="Units per period"
                                      type="unitsPerPeriod"
                                    />
                                  </th>
                                  <th>
                                    <OrderItemsHeaderTooltip
                                      label="No. of periods"
                                      type="numberOfPeriods"
                                    />
                                  </th>
                                  <th className="text-right-align">
                                    <OrderItemsHeaderTooltip
                                      label="Total cost"
                                      type="totalCost"
                                    />
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {order.OrderDetails &&
                                  order.OrderDetails.length > 0 &&
                                  order.OrderDetails.map((detail: any) => {
                                    return (
                                      <ProductAccordian
                                        key={detail.ProductName}
                                        productName={detail.ProductName}
                                        skuDetails={detail.SkuDetails}
                                        showEngagementColumn={true}
                                        isOrderpipelineOrderTab={true}
                                        showQuantityColumn={false}
                                      />
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                        </>
                      </td>
                    </tr>
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
};

export default CancelledOrdersTable;
